import { Autocomplete } from "@base-ui/react/autocomplete";
import classNames from "classnames";
import { Plus, X } from "lucide-react";
import * as React from "react";

import { useDebouncedCallback } from "@/shared/hooks/useDebouncedCallback";

import styles from "./TagInput.module.css";

/** Control density, on the same scale as `TextInput`, `Select` and `Combobox`. */
export type TagInputSize = "sm" | "md" | "lg";

const sizeClass: Record<TagInputSize, string> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

// Trailing debounce applied to the draft before an async suggestion source is
// re-invoked, so a burst of typing collapses to one request. Matches the
// filter pickers' server-search debounce.
const SUGGESTION_DEBOUNCE_MS = 300;

/**
 * Where the draft's completions come from: a fixed list matched client-side by
 * case-insensitive substring, or a fetch keyed on the (debounced) draft text.
 */
export type TagSuggestionsResolver = (
  query: string,
  signal: AbortSignal,
) => Promise<readonly string[]>;

export type TagSuggestions = readonly string[] | TagSuggestionsResolver;

/**
 * One row of the popup: a suggestion, or the trailing "use what I typed" row.
 * `custom` marks the latter, which carries the same text as a suggestion might
 * — the flag, not the value, is what keeps the two rows distinct.
 */
interface SuggestionItem {
  value: string;
  custom: boolean;
}

export interface TagInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "value" | "onChange" | "defaultValue" | "size"
  > {
  /** The committed tags. Controlled: the field renders what it is given. */
  tags: string[];
  /** Called with the full updated tag list whenever a tag is added or removed. */
  onChange: (tags: string[]) => void;
  /**
   * Placeholder shown in the draft input when there are no tags yet.
   * @default "Add tags…"
   */
  placeholder?: string;
  /**
   * Minimum height, padding, and font size, so a tag field lines up with the
   * other fields of its size.
   * @default "md"
   */
  size?: TagInputSize;
  /**
   * Offer completions for the draft. Absent: a plain draft field, with no
   * popup at all.
   *
   * A function is called with the debounced draft and an `AbortSignal`
   * (aborted when the draft moves on or the field unmounts); it returns
   * already-matching suggestions, rendered verbatim. A static list is matched
   * client-side by case-insensitive substring. Tags already committed are
   * never offered, and a trailing row always offers the draft itself, so text
   * nothing suggests is still one Enter away.
   */
  suggestions?: TagSuggestions;
}

interface SuggestionState {
  items: readonly string[];
  isLoading: boolean;
  isError: boolean;
}

const NO_SUGGESTIONS: SuggestionState = {
  items: [],
  isLoading: false,
  isError: false,
};

/**
 * Asks a resolver for the debounced draft, holding the latest answer. Each new
 * query aborts the one before it, so a slow early page can't land on top of a
 * later one. Fetched with a plain effect rather than a query: results are
 * ephemeral, so a cache would buy nothing, and the resolver is read through a
 * ref so an inline arrow doesn't refetch every render.
 */
function useResolvedSuggestions(
  resolve: TagSuggestionsResolver | undefined,
  draft: string,
): SuggestionState {
  const resolverRef = React.useRef(resolve);
  resolverRef.current = resolve;
  const enabled = resolve !== undefined;

  const [query, setQuery] = React.useState("");
  const debounceQuery = useDebouncedCallback(setQuery, SUGGESTION_DEBOUNCE_MS);
  React.useEffect(() => {
    if (enabled) {
      debounceQuery(draft.trim());
    }
  }, [draft, enabled, debounceQuery]);

  const [resolved, setResolved] = React.useState(NO_SUGGESTIONS);
  // The empty query is asked too: it is the "show me everything" page a
  // freshly focused field opens with.
  React.useEffect(() => {
    const currentResolve = resolverRef.current;
    if (currentResolve === undefined) {
      setResolved(NO_SUGGESTIONS);
      return;
    }
    const controller = new AbortController();
    setResolved((previous) => ({
      ...previous,
      isLoading: true,
      isError: false,
    }));
    currentResolve(query, controller.signal)
      .then((items) => {
        if (!controller.signal.aborted) {
          setResolved({ items, isLoading: false, isError: false });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setResolved({ items: [], isLoading: false, isError: true });
        }
      });
    return () => controller.abort();
  }, [query, enabled]);

  return resolved;
}

/** The entries of a fixed list containing the draft, case-insensitively. */
function matchStatic(list: readonly string[], draft: string): SuggestionState {
  const needle = draft.trim().toLowerCase();
  return {
    items: list.filter((option) => option.toLowerCase().includes(needle)),
    isLoading: false,
    isError: false,
  };
}

/** The suggestions currently on offer for `draft`, in either source's form. */
function useSuggestions(
  suggestions: TagSuggestions | undefined,
  draft: string,
): SuggestionState {
  const resolve = typeof suggestions === "function" ? suggestions : undefined;
  const resolved = useResolvedSuggestions(resolve, draft);
  const list = typeof suggestions === "function" ? undefined : suggestions;
  return React.useMemo(
    () => (list === undefined ? resolved : matchStatic(list, draft)),
    [list, resolved, draft],
  );
}

/**
 * The popup's rows: every suggestion not already committed, then the draft
 * itself. The draft row is for text nothing offers — an exact suggestion IS
 * the draft, so it would be the same commit twice over.
 */
function buildRows(
  suggested: readonly string[],
  tags: readonly string[],
  draft: string,
): SuggestionItem[] {
  const rows: SuggestionItem[] = suggested
    .filter((option) => !tags.includes(option))
    .map((option) => ({ value: option, custom: false }));
  if (draft !== "" && !tags.includes(draft) && !suggested.includes(draft)) {
    rows.push({ value: draft, custom: true });
  }
  return rows;
}

/**
 * A tag/chip input primitive. Renders committed tags as removable pills plus
 * a draft text field for adding new ones — Enter or comma commits the draft,
 * Backspace on an empty draft removes the last tag.
 *
 * Controlled: render the tags you are given and take updates from `onChange`.
 *
 * With `suggestions`, the draft field also offers completions in a popup,
 * below which a final row commits the draft verbatim. Nothing is highlighted
 * by default, so Enter always commits exactly what was typed unless the user
 * arrowed onto a row first.
 *
 * The forwarded ref attaches to the draft `<input>`, and any additional
 * native input props (`id`, `aria-label`, `aria-describedby`, `disabled`,
 * `name`, etc.) are passed through.
 *
 * @example
 * ```tsx
 * <TagInput tags={tags} onChange={setTags} suggestions={searchTags} />
 * ```
 */
export const TagInput = React.forwardRef<HTMLInputElement, TagInputProps>(
  function TagInput(
    {
      tags,
      onChange,
      placeholder = "Add tags…",
      size = "md",
      suggestions,
      ...rest
    },
    ref,
  ) {
    const [draft, setDraft] = React.useState("");
    const [announcement, setAnnouncement] = React.useState("");
    // What Base UI has highlighted, read synchronously inside the Enter
    // handler: Enter is dual-routed, so with a row highlighted that row's
    // `onClick` commits and the free-text branch must stand down.
    const highlighted = React.useRef<SuggestionItem | undefined>(undefined);
    const popupRef = React.useRef<HTMLDivElement>(null);
    // The popup is anchored to the whole field, not Base UI's default of the
    // draft `<input>`, so it spans the field's width rather than the draft's.
    const fieldRef = React.useRef<HTMLDivElement>(null);
    // Owned here so focusing the draft opens the popup on everything the
    // source knows; Base UI's own open/close transitions are mirrored in.
    const [open, setOpen] = React.useState(false);

    const {
      items: suggested,
      isLoading,
      isError,
    } = useSuggestions(suggestions, draft);

    const trimmedDraft = draft.trim();
    const items = React.useMemo(
      () => buildRows(suggested, tags, trimmedDraft),
      [suggested, tags, trimmedDraft],
    );
    const hasPopup = suggestions !== undefined;

    // Suggestions commit verbatim: they are names that already exist, so
    // trimming or stripping a delimiter would target a different tag.
    const commitTag = (tag: string) => {
      setDraft("");
      highlighted.current = undefined;
      if (!tag) {
        return;
      }
      if (tags.includes(tag)) {
        setAnnouncement(`"${tag}" is already added`);
        return;
      }
      onChange([...tags, tag]);
      setAnnouncement(`Added "${tag}"`);
    };

    // The draft is typed text, so it is tidied first: the comma that
    // committed it, and surrounding whitespace, are delimiters, not name.
    const commitDraft = () => {
      commitTag(draft.trim().replace(/,$/, ""));
    };

    const removeTag = (tag: string) => {
      onChange(tags.filter((t) => t !== tag));
      setAnnouncement(`Removed "${tag}"`);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        if (highlighted.current !== undefined) {
          return;
        }
        event.preventDefault();
        commitDraft();
      } else if (event.key === ",") {
        event.preventDefault();
        commitDraft();
      } else if (event.key === "Backspace" && !draft && tags.length) {
        const removed = tags[tags.length - 1];
        onChange(tags.slice(0, -1));
        setAnnouncement(`Removed "${removed}"`);
      }
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      const next = event.relatedTarget as Node | null;
      if (
        event.currentTarget.parentElement?.contains(next) ||
        popupRef.current?.contains(next)
      ) {
        return;
      }
      commitDraft();
    };

    const isEmpty = tags.length === 0;
    const draftInput = !hasPopup ? (
      <input
        {...rest}
        ref={ref}
        className={styles.draft}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={isEmpty ? placeholder : ""}
      />
    ) : (
      <Autocomplete.Input
        {...rest}
        ref={ref}
        className={styles.draft}
        onKeyDown={handleKeyDown}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        placeholder={isEmpty ? placeholder : ""}
      />
    );

    const field = (
      <div
        ref={fieldRef}
        className={classNames(styles.tagInput, sizeClass[size])}
        role="group"
        aria-label="Tags"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            (e.currentTarget as HTMLElement).querySelector("input")?.focus();
          }
        }}
      >
        <ul className={styles.pillList}>
          {tags.map((t) => (
            <li key={t} className={styles.pill}>
              {t}
              <button
                type="button"
                className={styles.pillRemove}
                onClick={() => removeTag(t)}
                aria-label={`Remove ${t}`}
                tabIndex={-1}
              >
                <X size={10} />
              </button>
            </li>
          ))}
        </ul>
        {draftInput}
        <span className="sr-only" aria-live="polite">
          {announcement}
        </span>
      </div>
    );

    if (!hasPopup) {
      return field;
    }

    return (
      <Autocomplete.Root
        items={items}
        value={draft}
        // Held shut while there is nothing to show, so a focused field with
        // nothing to suggest doesn't open an empty box.
        open={open && (isLoading || isError || items.length > 0)}
        onOpenChange={setOpen}
        openOnInputClick
        // The rows are assembled here — committed tags dropped, the draft row
        // appended — so Base UI must render them verbatim.
        mode="none"
        filter={null}
        itemToStringValue={(item: SuggestionItem) => item.value}
        onValueChange={(next, details) => {
          // `item-press` is Base UI writing the pressed row's text back into
          // the input; the row's `onClick` already committed and cleared it.
          if (details.reason === "item-press") {
            return;
          }
          setDraft(next);
        }}
        onItemHighlighted={(item: SuggestionItem | undefined) => {
          highlighted.current = item;
        }}
      >
        {field}
        <Autocomplete.Portal>
          <Autocomplete.Positioner
            className={styles.positioner}
            anchor={fieldRef}
            sideOffset={4}
            collisionPadding={8}
          >
            <Autocomplete.Popup
              ref={popupRef}
              className={styles.popup}
              aria-busy={isLoading}
            >
              <Autocomplete.Status className={styles.status}>
                {isError ? "Suggestions are unavailable" : null}
              </Autocomplete.Status>
              {isLoading && (
                <div
                  className={styles.skeleton}
                  data-testid="tag-input-loading"
                  role="presentation"
                >
                  <span className={styles.skeletonRow} />
                  <span className={styles.skeletonRow} />
                </div>
              )}
              <Autocomplete.List
                className={styles.list}
                hidden={isLoading || undefined}
              >
                {(item: SuggestionItem) => (
                  <Autocomplete.Item
                    key={item.custom ? `use-draft:${item.value}` : item.value}
                    value={item}
                    className={styles.item}
                    data-testid={
                      item.custom
                        ? "tag-input-use-draft"
                        : "tag-input-suggestion"
                    }
                    onClick={() => commitTag(item.value)}
                  >
                    {item.custom ? (
                      <CustomItem value={item.value} />
                    ) : (
                      item.value
                    )}
                  </Autocomplete.Item>
                )}
              </Autocomplete.List>
            </Autocomplete.Popup>
          </Autocomplete.Positioner>
        </Autocomplete.Portal>
      </Autocomplete.Root>
    );
  },
);

/** The trailing "use what I typed" row: the draft, marked as an addition. */
function CustomItem({ value }: { value: string }) {
  return (
    <span className={styles.customItem}>
      <Plus className={styles.customItemIcon} aria-hidden />
      <span style={{ lineHeight: 0 }}>
        Use <b>“{value}”</b>
      </span>
    </span>
  );
}
